import React, { useCallback, useState } from 'react';
import {
  FDIcon, FDBodyText, FDStack, FDPaper,
} from '@facet-wealth/design';
import MuiAutocomplete from '@material-ui/lab/Autocomplete';
import useInstitutionSearch from 'Hooks/useInstitutionSearch';
import TextFieldWithLabelAbove from 'Components/ComponentLibrary/Components/Uncategorized/TextFieldWithLabelAbove';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useIsDigitalOnboarding from 'Hooks/useIsDigitalOnboarding';
import FDTextField from 'Components/ComponentLibrary/Components/Uncategorized/FDTextField';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { dataAttrs } from 'Helpers/Testing';

const OTHER_FINANCIAL_INSTITUTION_ID = '-1';

const useStyles = buildUseStyles('MeetingTimeDetail', (theme) => ({
  digitalOnboardingInput: {
    '& .MuiAutocomplete-inputRoot.MuiFilledInput-root': {
      padding: 'Opx !important',
    },
    '& .MuiFilledInput-root': {
      padding: theme.spacing(4),

    },
  },
}));


function SearchableFinancialInstitutionQuerySelect(resourceProps) {
  const classes = useStyles();
  const isDigitalOnboarding = useIsDigitalOnboarding();
  const { displayErrorMessage } = useSnackbarContext();

  const {
    readonly,
    ...rest
  } = resourceProps;

  const { resource, handleChange: handleAttrChange } = useMetaLayoutContext();

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);


  const onSearch = useCallback((searchTerm) => resource.query(
    'getFinancialInstitutions',
    (link) => {
      const linkWithStepType = link.withQueryParams().like('search', searchTerm);
      return linkWithStepType.get();
    },
    () => { throw new Error('Expected the getFinancialInstitutions query'); },
  ), []);

  const handleFilterInstitutions = useCallback(async (searchTerm) => {
    if (searchTerm.length < 1) { // Prevents searching on empty or very short strings
      setOptions([]);
      return;
    }
    setLoading(true);
    try {
      if (resource.attr('searchableFinancialInstitutionID') === OTHER_FINANCIAL_INSTITUTION_ID) {
        return;
      }
      const response = await onSearch(searchTerm);
      const returnedOptions = response.resources().list().map(option => {
        return {
          id: option.id(),
          name: option.attr('name'),
          // search: searchTerm,
          logo: option.attr('logo') !== '' ? option.attr('logo') : option.attr('icon'),
        };
      });
      setOptions(returnedOptions);
    } catch (error) {
      displayErrorMessage(error, displayErrorMessage, 'Error filtering institutions.');
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [onSearch]);


  // Use the provided hook
  const { institutionName, handleChange } = useInstitutionSearch(handleFilterInstitutions, resource.meta().field('searchableFinancialInstitutionID').attr('prompt'));

  React.useEffect(() => {
    if (resource.meta().field('searchableFinancialInstitutionID').attr('prompt') !== '') {
      handleChange(resource.meta().field('searchableFinancialInstitutionID').attr('prompt'));
    }
  }, []);

  React.useEffect(() => {
    if (institutionName === '') {
      handleAttrChange('searchableFinancialInstitutionID', '');
    }
  }, [institutionName]);

  const CustomPaper = React.forwardRef((props, ref) => {
    const { children } = props;
    return <FDPaper appearance={'elevated'} color={'subtle'} ref={ref}> {children} </FDPaper>;
  });

  return (
    <MuiAutocomplete
      disabled={readonly}
      autoComplete
      // options prop expects an array of objects
      defaultValue={resource.meta().field('searchableFinancialInstitutionID').attr('prompt')}
      value={resource.meta().field('searchableFinancialInstitutionID').attr('prompt')}
      options={options}
      includeInputInList
      getOptionSelected={(option, value) => {
        return option.id === value.id;
      }}
      // getOptionLabel tells the component how to display each option
      getOptionLabel={(option) => {
        if (option.id === OTHER_FINANCIAL_INSTITUTION_ID) {
          return `Add ${option.name}`;
        }
        return option.name;
      }}
      noOptionsText={<FDBodyText color={'textSecondary'} value={'No options'} />}
      filterOptions={(x) => x}
      loading={loading}
      // This is the key handler for search input
      onInputChange={(event, newInputValue) => {
        handleChange(newInputValue);
      }}
      onChange={(event, newValue) => {
        if (newValue.id === OTHER_FINANCIAL_INSTITUTION_ID) {
          handleAttrChange('otherFinancialInstitution', newValue.name);
        }
        handleAttrChange('searchableFinancialInstitutionID', newValue.id);
      }}
      // Add inputValue prop to explicitly control the input value
      inputValue={institutionName}
      PaperComponent={CustomPaper}
      clearOnBlur={false}
      clearOnEscape={false}
      renderInput={(params) => {
        if (isDigitalOnboarding) {
          return (<TextFieldWithLabelAbove
            {...params}
            {...rest}
            disabled={readonly}
            label="Search for an institution"
            InputProps={{
              className: classes.digitalOnboardingInput,
              ...params.InputProps,
              readOnly: readonly,
            }}
          />);
        }

        return (<FDTextField
          {...params}
          {...rest}
          label="Search for an institution"
          disabled={readonly}
          InputProps={{
            ...params.InputProps,
            readOnly: readonly,
          }}
        />);
      }}
      renderOption={(props) => {
        return (
          <FDStack direction={'row'} spacing={1} alignSelf={'center'} alignItems={'center'} align={'center'} {...dataAttrs('institution-option')}>
            {props.id === OTHER_FINANCIAL_INSTITUTION_ID
              ? <FDIcon icon={'add'} />
              : null
          }
            {props.logo !== '' && props.id !== OTHER_FINANCIAL_INSTITUTION_ID
              ? <img
                alt={`${props.name} logo`}
                src={props.logo}
                style={{ width: 50, height: 30, borderRadius: '20px' }}
              />
              : null
          }
            <FDBodyText value={props.id === OTHER_FINANCIAL_INSTITUTION_ID ? `Add ${props.name}` : props.name} />
          </FDStack>
        );
      }}
    />
  );
}

SearchableFinancialInstitutionQuerySelect.defaultProps = {
};

SearchableFinancialInstitutionQuerySelect.propTypes = {
};

export default SearchableFinancialInstitutionQuerySelect;
